import { DefaultErrorShape, ProcedureBuilder, ProcedureParams, RootConfig, TRPCError } from '@trpc/server';
import { TRPC_ERROR_CODE_KEY } from '@trpc/server/rpc';
import { NextApiRequest } from 'next';
import { TRPCClientErrorBase } from '@trpc/client';
import { ZodTypeAny, z } from 'zod';
import SuperJSON from 'superjson';

import { BffError, BffErrorCode, BffUserError } from './errors';
import { Context } from './context';

export const MyOmersBffSystemErrorLogSchema = z.object({
  message: z.object({
    code: z.string(),
    error: z.string(),
    cause: z.string(),
    module: z.string(),
    route: z.string(),
    httpRequestBody: z.string(),
    partyNumber: z.string(),
    correlationId: z.string(),
    datadogTraceId: z.string(),
    userIpAddress: z.string()
  }),
  level: z.string().optional(),
  service: z.string().optional(),
  timestamp: z.string().optional()
});

/**
 * Shape of the error log generated from a BffError
 */
export type MyOmersBffSystemErrorLog = Omit<z.infer<typeof MyOmersBffSystemErrorLogSchema>, 'message.code'> & {
  message: { code: BffErrorCode };
};

/**
 * When we want to return an error response to the client from the tRPC server,
 * we have to throw a tRPC error. However, we also want to include our custom
 * error type. This subclass extends the tRPC error type with extra information
 * specific to our app
 */
export class MyOmersTRPCError extends TRPCError {
  public bffError: BffError | BffUserError;
  public log: MyOmersBffSystemErrorLog | undefined;
  public correlationId: string | undefined;
  public datadogTraceId: string | undefined;

  constructor(opts: {
    message: string;
    code: TRPC_ERROR_CODE_KEY;
    cause?: unknown;
    bffError: BffError | BffUserError;
    log?: MyOmersBffSystemErrorLog;
    correlationId?: string;
    datadogTraceId?: string;
  }) {
    super(opts);
    this.bffError = opts.bffError;
    this.log = opts.log;
    this.correlationId = opts.correlationId;
    this.datadogTraceId = opts.datadogTraceId;
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/**
 * When we initialize tRPC in`src/server/trpc.ts` we define a custom error
 * formatter.
 *
 * We want to include some extra structured data in our formatted errors other
 * than what's available in the default error shape - so we define a
 * custom error shape here.
 *
 * This isn't defined in the `src/server/trpc.ts` file directly because we also
 * want to import it in other places too (such as components that expect to
 * receive this error shape from failed trpc procedure calls.)
 */
export type FormattedMyOmersErrorShape = DefaultErrorShape & {
  data: {
    bff?: {
      code: BffErrorCode | BffUserError;
      message: string;
      errorKind: 'system' | 'user';
    };
    debug?: {
      correlationId?: string;
      datadogTraceId?: string;
      ERROR_LOG___ONLY_AVAILABLE_IN_LOWER_ENVIRONMENTS?: MyOmersBffSystemErrorLog;
    };
  };
};

/**
 * Type for the `{ ctx, input }` parameter in a tRPC procedure.
 *
 * Note: This type definition is a crime against humanity. If you're reading
 * this, I'm so sorry. It's the only way I was able to infer the type of `ctx`
 * from a generic type argument.
 *
 * There is some hope though - if we ever upgrade to  tRPC 11, this type can be
 * removed and replaced by one provided by tRPC themselves:
 * https://trpc.io/docs/v11/server/procedures#inferProcedureBuilderResolverOptions
 */
export type ProcedureOpts<
  Procedure extends ProcedureBuilder<
    ProcedureParams<
      RootConfig<{
        ctx: Context;
        meta: { description?: string | undefined }; // NOSONAR https://www.typescriptlang.org/tsconfig/#exactOptionalPropertyTypes
        errorShape: FormattedMyOmersErrorShape;
        transformer: typeof SuperJSON;
      }>,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      any
    >
  >,
  Input extends ZodTypeAny
> = {
  ctx: Parameters<Parameters<Procedure['subscription']>['0']>['0']['ctx'];
  input: z.infer<Input>;
};

/**
 * This is the type of an error returned from a tRPC call in myOMERS such as
 * this:
 * ```ts
 * const { error } = trpc.common.hello.useQuery();
 * ```
 */
export type MyOmersTRPCClientError = TRPCClientErrorBase<FormattedMyOmersErrorShape>;
/**
 * Next request object (for both API requests and Get Server Side Props requests)
 */
export type NextRequest = NextApiRequest;

/**
 * Utility type to require all fields of an interface be non-nullable and non-undefined
 *
 * https://stackoverflow.com/a/73083447/7003127
 */
export type NonNullableFields<T> = {
  [P in keyof T]: NonNullable<T[P]>;
};
